import { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  Stack,
  Group,
  Text,
  TextInput,
  Button,
  Badge,
  Loader,
  Switch,
  Divider,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  IconCheck,
  IconX,
  IconWorld,
  IconBuildingStore,
  IconDeviceTv,
  IconShieldCheck,
  IconUserCheck,
} from '@tabler/icons-react';
import { useResellerBrand } from '@/shared/hooks/useResellerBrand';
import {
  APP_CONSTRAINTS,
  APP_STATE_TYPE_ID,
  APP_WEB_DOMAIN_LABELS,
  APP_WEB_SHARED_DOMAIN_ID,
} from '@/lib/constants/app';
import type { FactoryStoreKey } from '@/lib/constants/appFactory';
import { AppPlatformUtils } from '../AppPlatformUtils';
import { AppUtils } from '../AppUtils';
import {
  useCheckCustomNameAvailability,
  useCreateAppWeb,
  useCreateAppRoku,
  useUpsertAppListing,
  useUpdateApp,
} from '../useAppMutations';
import {
  appListingFormSchema,
  type AppListingFormSchemaData,
} from '../schemas/AppListingForm.schema';
import {
  appRokuSetupFormSchema,
  type AppRokuSetupFormSchemaData,
} from '../schemas/AppRokuForm.schema';
import { AppListingFields } from './AppListingFields';
import { PrivacyPolicyStep } from '../legal/PrivacyPolicyStep';
import { StoreAccountStep } from './StoreAccountStep';
import type { AppCfgsDto, AppCfgWebCreateReq, AppListingFormData } from '@/lib/types/App';

export type WizardStep = 'domain' | 'listing' | 'privacy' | 'store' | 'roku';

const STEP_ORDER: WizardStep[] = ['domain', 'listing', 'privacy', 'store', 'roku'];

interface AppRokuSetupWizardProps {
  opened: boolean;
  app: AppCfgsDto;
  onClose: () => void;
  /** Called after the Roku platform row is created — setup complete. */
  onFinished: () => void;
  /** Open at a specific step (the Status tab's readiness chips); default = the first unsatisfied step. */
  initialStep?: WizardStep;
  /**
   * The store-onboarding steps alone (address, listing, privacy policy, store
   * account) for a platform that is not Roku: no Channel step, the store step
   * finishes. `stores` = the stores that platform ships to.
   */
  storeSetupOnly?: boolean;
  stores?: FactoryStoreKey[];
}

/**
 * Roku onboarding wizard: Domain → Store Listing → Privacy policy → Store
 * account → Channel. The domain step creates the app_cfg_web row — the app's
 * single tenancy anchor (Roku builds fail closed without it) — with an
 * explicit "Enable website" toggle, so OTT-only customers get an address
 * without a public website. Steps 3–4 are the store-onboarding pair (the
 * generated privacy policy, the delegated developer account); both can be
 * finished later from the Status tab, and the wizard reopens at either one
 * after the channel exists.
 */
export function AppRokuSetupWizard({
  opened,
  app,
  onClose,
  onFinished,
  initialStep,
  storeSetupOnly = false,
  stores,
}: AppRokuSetupWizardProps) {
  const hasWeb = app.web != null;
  const hasRoku = app.roku != null;
  const firstStep = (): WizardStep => initialStep ?? (hasWeb ? 'listing' : 'domain');
  const [step, setStep] = useState<WizardStep>(firstStep);

  // Re-open starts at the requested step, else the first unsatisfied one
  useEffect(() => {
    if (opened) setStep(initialStep ?? (app.web != null ? 'listing' : 'domain'));
  }, [opened, app.web, initialStep]);

  const stepIndex = STEP_ORDER.indexOf(step);
  // After the channel exists the last step is done: the store step finishes the wizard.
  const storeIsLast = hasRoku || storeSetupOnly;
  const afterStore = storeIsLast ? onFinished : () => setStep('roku');

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={storeIsLast ? 'Store Setup' : 'Set Up Roku App'}
      size="lg"
      closeOnClickOutside={false}
    >
      <Stack gap="md">
        {/* Step indicator */}
        <Group gap="xs">
          <StepBadge label="1. Domain" icon={<IconWorld size={12} />} active={step === 'domain'} done={stepIndex > 0 || hasWeb} />
          <StepBadge label="2. Store Listing" icon={<IconBuildingStore size={12} />} active={step === 'listing'} done={stepIndex > 1} />
          <StepBadge label="3. Privacy policy" icon={<IconShieldCheck size={12} />} active={step === 'privacy'} done={stepIndex > 2} />
          <StepBadge label="4. Store account" icon={<IconUserCheck size={12} />} active={step === 'store'} done={stepIndex > 3} />
          {!storeSetupOnly && (
            <StepBadge label="5. Channel" icon={<IconDeviceTv size={12} />} active={step === 'roku'} done={hasRoku} />
          )}
        </Group>
        <Divider />

        {step === 'domain' && (
          <DomainStep app={app} onNext={() => setStep('listing')} />
        )}
        {step === 'listing' && (
          <ListingStep
            app={app}
            onBack={app.web == null ? () => setStep('domain') : undefined}
            onNext={() => setStep('privacy')}
          />
        )}
        {step === 'privacy' && (
          <PrivacyPolicyStep app={app} onBack={() => setStep('listing')} onNext={() => setStep('store')} />
        )}
        {step === 'store' && (
          <StoreAccountStep
            app={app}
            stores={stores}
            onBack={() => setStep('privacy')}
            onNext={afterStore}
            nextLabel={storeIsLast ? 'Done' : 'Next'}
          />
        )}
        {step === 'roku' && (
          <RokuStep app={app} onBack={() => setStep('store')} onFinished={onFinished} />
        )}
      </Stack>
    </Modal>
  );
}

function StepBadge({
  label,
  icon,
  active,
  done,
}: {
  label: string;
  icon: React.ReactNode;
  active: boolean;
  done: boolean;
}) {
  return (
    <Badge
      variant={active ? 'filled' : 'light'}
      color={done && !active ? 'green' : active ? 'blue' : 'gray'}
      leftSection={icon}
    >
      {label}
    </Badge>
  );
}

// ── Step 1: Domain (tenancy anchor + optional website) ──

function DomainStep({ app, onNext }: { app: AppCfgsDto; onNext: () => void }) {
  const createWebMutation = useCreateAppWeb(app.appCfg.core.appCfgId);
  const [customName, setCustomName] = useState('');
  const [enableWebsite, setEnableWebsite] = useState(true);

  const isReseller = useResellerBrand();
  const defaultDomainId = isReseller
    ? APP_WEB_SHARED_DOMAIN_ID.CloudsiteDotApp
    : APP_WEB_SHARED_DOMAIN_ID.VideositeDotApp;
  const domainLabel = APP_WEB_DOMAIN_LABELS[defaultDomainId];

  const {
    data: availResult,
    isLoading: isChecking,
    isFetched,
  } = useCheckCustomNameAvailability(customName);
  const isAvailable = availResult?.isAvailable ?? false;
  const showAvailability = customName.trim().length >= 2 && isFetched;

  // Web row already exists — nothing to create here
  if (app.web) {
    return (
      <Stack gap="md">
        <Text size="sm">
          Your app already has its address:{' '}
          <Badge variant="light" color="blue" size="lg">
            {app.web.core.customName}.{APP_WEB_DOMAIN_LABELS[app.web.core.sharedDomainId]}
          </Badge>
        </Text>
        <Group justify="flex-end">
          <Button onClick={onNext}>Next</Button>
        </Group>
      </Stack>
    );
  }

  const handleCreate = () => {
    const req: AppCfgWebCreateReq = {
      appCfgId: app.appCfg.core.appCfgId,
      web: {
        core: {
          appStateTypeId: enableWebsite ? APP_STATE_TYPE_ID.Active : APP_STATE_TYPE_ID.Inactive,
          customName: customName.trim(),
          sharedDomainId: defaultDomainId as 1 | 2 | 3,
          customDomain: null,
        },
        // New web apps start in Dark Mode (owner decision 2026-09-24: the majority case).
        style: { styleIsDarkMode: true, styleBGColorHex: null, styleTextColorHex: null },
        seo: { allowIndex: true },
      },
    };
    createWebMutation.mutate(req, {
      onSuccess: () => onNext(),
      onError: (err) => notifications.show({ title: 'Error', message: err.message, color: 'red' }),
    });
  };

  const previewDomain = customName.trim()
    ? `${customName.trim().toLowerCase()}.${domainLabel}`
    : `your-subdomain.${domainLabel}`;

  return (
    <Stack gap="md">
      <Text size="sm" c="dimmed">
        Every app needs its own address — it anchors your app across TV platforms and the
        web. Choose a subdomain first.
      </Text>

      <TextInput
        label="Subdomain"
        placeholder="my-app"
        description={`${customName.length}/${APP_CONSTRAINTS.CUSTOM_NAME_MAX} — Lowercase letters, numbers, and hyphens`}
        required
        maxLength={APP_CONSTRAINTS.CUSTOM_NAME_MAX}
        value={customName}
        rightSection={
          isChecking ? (
            <Loader size={14} />
          ) : showAvailability ? (
            isAvailable ? (
              <IconCheck size={14} color="green" />
            ) : (
              <IconX size={14} color="red" />
            )
          ) : null
        }
        onChange={(e) => {
          const sanitized = e.currentTarget.value
            .toLowerCase()
            .replace(/[^a-z0-9-]/g, '')
            .replace(/^-+/, '')
            .replace(/-{2,}/g, '-');
          setCustomName(sanitized);
        }}
      />

      {showAvailability && !isAvailable && (
        <Text size="sm" c="red">
          This subdomain is already taken. Please choose another.
        </Text>
      )}

      <Group gap="xs">
        <Text size="sm" c="dimmed">
          Your app&apos;s address:
        </Text>
        <Badge variant="light" color="blue" size="lg">
          {previewDomain}
        </Badge>
      </Group>

      <Switch
        label="Enable website"
        description="Also publish a browsable website at this address. Turn off to use the address for TV apps only — you can enable the website later."
        checked={enableWebsite}
        onChange={(e) => setEnableWebsite(e.currentTarget.checked)}
      />

      <Group justify="flex-end">
        <Button
          onClick={handleCreate}
          loading={createWebMutation.isPending}
          disabled={!isAvailable || !customName.trim()}
        >
          Claim Domain
        </Button>
      </Group>
    </Stack>
  );
}

// ── Step 2: Store listing + child-targeted flag ──

function ListingStep({
  app,
  onBack,
  onNext,
}: {
  app: AppCfgsDto;
  onBack?: () => void;
  onNext: () => void;
}) {
  const appCfgId = app.appCfg.core.appCfgId;
  const upsertListingMutation = useUpsertAppListing(appCfgId);
  const updateAppMutation = useUpdateApp(appCfgId);
  const [isChildTargeted, setIsChildTargeted] = useState(app.appCfg.core.isChildTargeted);

  // Google TV's store needs the age rating before its first version (AppPlatformUtils).
  const ageRatingRequiredFor = useMemo(
    () => AppPlatformUtils.platformsRequiringAgeRating(app).map((info) => info.label),
    [app],
  );
  const listingSchema = useMemo(() => appListingFormSchema({ ageRatingRequiredFor }), [ageRatingRequiredFor]);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<AppListingFormSchemaData>({
    resolver: zodResolver(listingSchema),
    defaultValues: AppUtils.transformListingToFormData(app.listing),
  });

  const handleNext = handleSubmit((data) => {
    const req = AppUtils.transformListingFormToUpsertReq(data as AppListingFormData);
    upsertListingMutation.mutate(req, {
      onSuccess: () => {
        if (isChildTargeted !== app.appCfg.core.isChildTargeted) {
          updateAppMutation.mutate(
            AppUtils.buildChildTargetedUpdateReq(app, isChildTargeted),
            {
              onSuccess: () => onNext(),
              onError: (err) =>
                notifications.show({ title: 'Error', message: err.message, color: 'red' }),
            },
          );
        } else {
          onNext();
        }
      },
      onError: (err) => notifications.show({ title: 'Error', message: err.message, color: 'red' }),
    });
  });

  return (
    <Stack gap="md">
      <Text size="sm" c="dimmed">
        App stores ask for these details at submission. You can fill them in now or later on
        the Store Listing tab.
      </Text>

      <AppListingFields control={control} errors={errors} ageRatingRequiredFor={ageRatingRequiredFor} />

      <Switch
        label="Child-Targeted Content"
        description="Turn on if this app is directed at children — it changes how the app handles viewer data."
        checked={isChildTargeted}
        onChange={(e) => setIsChildTargeted(e.currentTarget.checked)}
      />

      <Group justify="space-between">
        {onBack ? (
          <Button variant="default" onClick={onBack}>
            Back
          </Button>
        ) : (
          <span />
        )}
        <Group gap="sm">
          <Button variant="default" onClick={onNext}>
            Skip for now
          </Button>
          <Button
            onClick={handleNext}
            loading={upsertListingMutation.isPending || updateAppMutation.isPending}
          >
            Save &amp; Continue
          </Button>
        </Group>
      </Group>
    </Stack>
  );
}

// ── Step 3: Roku channel ──

function RokuStep({
  app,
  onBack,
  onFinished,
}: {
  app: AppCfgsDto;
  onBack: () => void;
  onFinished: () => void;
}) {
  const createRokuMutation = useCreateAppRoku(app.appCfg.core.appCfgId);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<AppRokuSetupFormSchemaData>({
    resolver: zodResolver(appRokuSetupFormSchema),
    defaultValues: {
      title: app.appCfg.orgInfo.name.slice(0, APP_CONSTRAINTS.ROKU_TITLE_MAX),
      adUrl: '',
    },
  });

  const handleFinish = handleSubmit((data) => {
    const req = AppUtils.buildRokuCreateReq(app.appCfg.core.appCfgId, data.title, data.adUrl);
    createRokuMutation.mutate(req, {
      onSuccess: () => {
        notifications.show({
          title: 'Roku App Enabled',
          message: 'Roku platform has been enabled.',
          color: 'green',
        });
        onFinished();
      },
      onError: (err) => notifications.show({ title: 'Error', message: err.message, color: 'red' }),
    });
  });

  return (
    <Stack gap="md">
      <Controller
        name="title"
        control={control}
        render={({ field }) => (
          <TextInput
            label="Channel Name"
            description={`Shown in the Roku Channel Store and on the home screen — up to ${APP_CONSTRAINTS.ROKU_TITLE_MAX} characters`}
            required
            maxLength={APP_CONSTRAINTS.ROKU_TITLE_MAX}
            error={errors.title?.message}
            {...field}
          />
        )}
      />

      <Controller
        name="adUrl"
        control={control}
        render={({ field }) => (
          <TextInput
            label="Roku Ad URL"
            placeholder="https://..."
            description="Optional — VAST/VMAP ad tag used only by your Roku app. This is separate from the player ad settings, which apply to web embeds only."
            maxLength={APP_CONSTRAINTS.LISTING_URL_MAX}
            error={errors.adUrl?.message}
            {...field}
          />
        )}
      />

      <Group justify="space-between">
        <Button variant="default" onClick={onBack}>
          Back
        </Button>
        <Button onClick={handleFinish} loading={createRokuMutation.isPending}>
          Enable Roku App
        </Button>
      </Group>
    </Stack>
  );
}
